import React, { useRef, useState } from "react";
import { all_routes } from "../../router/all_routes";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import Swal from "sweetalert2";
import useApi from "../../../core/customhook/useApi";

const EmailVerification = () => {
  const navigation = useNavigate();
  const location = useLocation();
  const email = (location.state as { email?: string })?.email || "";

  const [otp, setOtp] = useState(Array(6).fill(""));
  const inputsRef = useRef<HTMLInputElement[]>([]);

  // useApi hook for verify OTP
  const { callApi, loading } = useApi("/api/verify-otp", {
    method: "POST",
    autoCall: false,
  });

  // Mask email for privacy display
  const maskEmail = (email: string) => {
    const [user, domain] = email.split("@");
    if (!user || !domain) return email;

    const maskedUser =
      user.length <= 2
        ? "*".repeat(user.length)
        : user[0] + "*".repeat(user.length - 2) + user[user.length - 1];

    return `${maskedUser}@${domain}`;
  };

  const handleChange = (element: HTMLInputElement, index: number) => {
    const val = element.value.replace(/\D/, ""); // Only digits
    if (!val) return;

    const newOtp = [...otp];
    newOtp[index] = val;
    setOtp(newOtp);

    if (index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
        inputsRef.current[index - 1]?.focus();
      }
    }
  };

  const navigationPath = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const enteredOtp = otp.join("");

    if (enteredOtp.length < 6) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete OTP",
        text: "Please enter the complete 6-digit OTP.",
        showConfirmButton: true,
      });
      return;
    }

    try {
      const payload = { email, otp: enteredOtp };
      const response = await callApi(payload);
      if (response.statusCode === 200) {
        localStorage.setItem("token", response.token);
      }
      Swal.fire({
        icon: "success",
        title: "OTP Verified",
        text: "You can now reset your password.",
        showConfirmButton: true,
        timer: 1000,
      });

      navigation(all_routes.resetPassword);
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Verification Failed",
        text:
          error?.response?.data?.message ||
          "Failed to verify OTP. Please try again.",
        showConfirmButton: true,
      });
    }
  };

  // Inside EmailVerification component:
  const { callApi: resendOtpApi, loading: resendLoading } = useApi(
    "/api/forgotPassword",
    {
      method: "POST",
      autoCall: false,
    }
  );

  const resendOtp = async (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    e.preventDefault();
    try {
      const response = await resendOtpApi({ email });

      Swal.fire({
        icon: "success",
        title: "OTP Resent successfully",
        text: response?.data?.message || "Please check your email to proceed.",
        showConfirmButton: true,
        timer: 1500,
        timerProgressBar: true,
        position: "center",
      });

      // Optionally reset OTP input on resend
      setOtp(Array(6).fill(""));

      // Optionally focus the first input box
      inputsRef.current[0]?.focus();
    } catch (err: any) {
      Swal.fire({
        icon: "error",
        title: "Failed to send OTP",
        text: err?.response?.data?.message || "Please try again.",
        showConfirmButton: true,
        position: "center",
      });
    }
  };

  return (
    <div className="container-fuild">
      <div className="login-wrapper w-100 overflow-hidden position-relative flex-wrap d-block vh-100">
        <div className="row">
          <div className="col-lg-5">
            <div className="login-background position-relative d-lg-flex align-items-center justify-content-center d-none flex-wrap vh-100">
              <div className="bg-overlay-img">
                <ImageWithBasePath
                  src="/assets/img/bg/bg-01.png"
                  className="bg-1"
                  alt="Img"
                />
                <ImageWithBasePath
                  src="/assets/img/bg/bg-02.png"
                  className="bg-2"
                  alt="Img"
                />
                <ImageWithBasePath
                  src="/assets/img/bg/bg-03.png"
                  className="bg-3"
                  alt="Img"
                />
              </div>
              <div className="authentication-card w-100">
                <div className="authen-overlay-item border w-100">
                  <h1 className="text-white display-1">
                    WeConnect: Uniting teams, streamlining human resource.
                  </h1>
                  <div className="my-4 mx-auto authen-overlay-img">
                    <ImageWithBasePath
                      src="/assets/img/weconnect_login.png"
                      alt="Img"
                    />
                  </div>
                  <div>
                    <p className="text-white fs-20 fw-semibold text-center">
                      Efficiently manage your workforce, streamline <br />{" "}
                      operations effortlessly.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-7 col-md-12 col-sm-12">
            <div className="row justify-content-center align-items-center vh-100 overflow-auto flex-wrap ">
              <div className="col-md-9 mx-auto p-4">
                <form>
                  <div>
                    <div className=" mx-auto mb-5 text-center">
                      <ImageWithBasePath
                        src="/assets/img/logo/weconnectlogo.png"
                        className="img-fluid"
                        alt="Logo"
                      />
                    </div>
                    <div className="card">
                      <div className="card-body p-4">
                        <div className=" mb-3">
                          <h2 className="mb-2 text-center">
                            Email OTP Verification
                          </h2>
                          <p className="mb-0 text-center">
                            Please enter the OTP received to confirm your
                            account ownership. A code has been sent to{" "}
                            <strong>{maskEmail(email)}</strong>
                          </p>
                        </div>
                        <div className="d-flex justify-content-center mb-3 gap-2">
                          {otp.map((digit: string, index: number) => (
                            <input
                              key={index}
                              type="text"
                              inputMode="numeric"
                              maxLength={1}
                              className="form-control text-center"
                              style={{ width: "45px", fontSize: "18px" }}
                              value={digit}
                              onChange={(e) => handleChange(e.target, index)}
                              onKeyDown={(e) => handleKeyDown(e, index)}
                              ref={(el) => (inputsRef.current[index] = el!)}
                              disabled={loading}
                            />
                          ))}
                        </div>
                        <div className="text-center mb-3">
                          <h6 className="fw-normal text-dark mb-0">
                            Didn't get the OTP?{" "}
                            <a href="#" className="hover-a" onClick={resendOtp}>
                              {resendLoading ? "Resending..." : "Resend OTP"}
                            </a>
                          </h6>
                        </div>

                        <button
                          type="submit"
                          className="btn btn-primary w-100"
                          onClick={navigationPath}
                          disabled={loading}
                        >
                          {loading ? "Verifying..." : "Verify & Proceed"}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
